import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  CalendarDays,
  MapPin,
  Clock,
  Users,
  Link as LinkIcon,
  Loader2,
  CheckCircle,
  XCircle,
  HelpCircle,
  Shirt,
  Package,
  DollarSign,
  ArrowLeft,
  Video,
} from 'lucide-react';
import api from '../../services/api';
import { formatDateInTimezone, formatTimeDisplay } from '../../utils/timezone';
import { sanitizeHtml } from '../../utils/sanitize';
import { useAuth } from '../../context/AuthContext';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';
// Default timezone for public pages (matches business default)
const DEFAULT_TIMEZONE = 'America/New_York';

const PublicEventRsvp = () => {
  const { token } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [verificationToken, setVerificationToken] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [businessTimezone, setBusinessTimezone] = useState(DEFAULT_TIMEZONE);
  const [accountEmail, setAccountEmail] = useState('');
  const [requestingLink, setRequestingLink] = useState(false);
  const [linkRequested, setLinkRequested] = useState(false);
  
  // RSVP Form
  const [rsvpData, setRsvpData] = useState({
    status: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    num_guests: 0,
    num_children: 0,
    notes: ''
  });
  
  // Media gallery
  const [selectedMedia, setSelectedMedia] = useState(0);

  useEffect(() => {
    fetchEvent();
    // Fetch timezone settings for public display
    fetchTimezone();
  }, [token]);

  useEffect(() => {
    const rawToken = new URLSearchParams(location.search).get('verify');
    setVerificationToken(rawToken);
    // The email link is a bearer token. Retain it only in React memory,
    // not browser history, copied URLs, or the visible address bar.
    if (rawToken) {
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.pathname, location.search]);

  const fetchTimezone = async () => {
    try {
      const response = await api.get('/admin/timezone-settings/public');
      setBusinessTimezone(response.data.timezone || DEFAULT_TIMEZONE);
    } catch (err) {
      // Use default timezone if API fails
      console.log('Using default timezone');
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await api.get(`/events/public/${token}`);
      setEvent(response.data);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError(err.response?.status === 404 
        ? 'Event not found or no longer available' 
        : 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSecureLink = async () => {
    if (!accountEmail.trim()) {
      toast.error('Enter the email address on your academy account');
      return;
    }
    setRequestingLink(true);
    try {
      await api.post(`/events/rsvp/private/${token}/request-link`, { email: accountEmail });
      setLinkRequested(true);
      toast.success('If this email matches an active account, a secure link is on its way.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to request a secure RSVP link');
    } finally {
      setRequestingLink(false);
    }
  };

  const handleSignIn = () => {
    const returnTo = `${location.pathname}${location.search}`;
    sessionStorage.setItem('vyomos_post_login_return', returnTo);
    window.location.assign(`/login?return_to=${encodeURIComponent(returnTo)}`);
  };

  const handleSubmitRsvp = async () => {
    if (!rsvpData.status) {
      toast.error('Please select your RSVP response');
      return;
    }
    const isPrivate = event.event_type === 'private';
    if (!isPrivate && !rsvpData.guest_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!isPrivate && !rsvpData.guest_email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (isPrivate && !user && !verificationToken) {
      toast.error('Sign in or use a secure email link to RSVP');
      return;
    }
    
    setSubmitting(true);
    try {
      if (isPrivate && user) {
        await api.post('/events/rsvp', {
          event_id: event.id,
          status: rsvpData.status,
          num_guests: rsvpData.num_guests,
          num_children: rsvpData.num_children,
          notes: rsvpData.notes,
        });
      } else if (isPrivate) {
        await api.post(`/events/rsvp/private/${token}`, {
          event_id: event.id,
          status: rsvpData.status,
          num_guests: rsvpData.num_guests,
          num_children: rsvpData.num_children,
          notes: rsvpData.notes,
          verification_token: verificationToken,
        });
      } else {
        await api.post(`/events/rsvp/public/${token}`, {
          event_id: event.id,
          ...rsvpData
        });
      }
      setSubmitted(true);
      toast.success('RSVP submitted successfully!');
    } catch (err) {
      console.error('RSVP error:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    // Use business timezone for display
    return formatDateInTimezone(isoString, businessTimezone, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    // Use business timezone for display
    return formatTimeDisplay(isoString, businessTimezone);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CalendarDays className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Event Not Found</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Link to="/">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const images = (event.media || []).filter(m => m.type === 'image');
  const video = (event.media || []).find(m => m.type === 'video');
  const allMedia = [...images, ...(video ? [video] : [])];
  
  const isRsvpClosed = event.rsvp_closed;
  const isFull = event.max_capacity && 
    (event.rsvp_counts?.total_attending || 0) >= event.max_capacity && 
    !event.enable_waitlist;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header - Back to Homepage left, Event Invitation badge centered */}
        <div className="relative flex items-center justify-center mb-6">
          <Link to="/" className="absolute left-0 inline-flex items-center text-emerald-600 hover:text-emerald-700">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Homepage
          </Link>
          <Badge className="bg-emerald-500">Event Invitation</Badge>
        </div>

        <Card className="overflow-hidden">
          {/* Media Gallery - Centered with proper sizing */}
          {allMedia.length > 0 && (
            <div className="bg-slate-100 flex flex-col items-center justify-center">
              <div className="w-full max-w-2xl mx-auto">
                {allMedia[selectedMedia]?.type === 'video' ? (
                  <video
                    src={`${API_URL}/api/events/${event.id}/media/${allMedia[selectedMedia].id}`}
                    className="w-full h-auto max-h-[450px] object-contain bg-black"
                    controls
                  />
                ) : (
                  <img
                    src={`${API_URL}/api/events/${event.id}/media/${allMedia[selectedMedia].id}`}
                    alt={event.title}
                    className="w-full h-auto max-h-[450px] object-contain"
                  />
                )}
              </div>
              
              {/* Thumbnails */}
              {allMedia.length > 1 && (
                <div className="flex gap-2 p-3 bg-slate-200 overflow-x-auto w-full justify-center">
                  {allMedia.map((media, idx) => (
                    <button
                      key={media.id}
                      onClick={() => setSelectedMedia(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-colors ${
                        selectedMedia === idx ? 'border-emerald-500' : 'border-transparent hover:border-slate-400'
                      }`}
                    >
                      {media.type === 'video' ? (
                        <div className="w-full h-full bg-slate-600 flex items-center justify-center">
                          <Video className="w-6 h-6 text-white" />
                        </div>
                      ) : (
                        <img
                          src={`${API_URL}/api/events/${event.id}/media/${media.id}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <CardContent className="p-6 sm:p-8">
            {/* Event Title & Description */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{event.title}</h1>
              {event.description && (
                <p className="text-slate-600" dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }} />
              )}
            </div>

            {/* Event Details Grid */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {/* Date & Time */}
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <CalendarDays className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">{formatDate(event.start_datetime)}</p>
                  <p className="text-sm text-slate-500">
                    {formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}
                  </p>
                  {event.is_multi_day && (
                    <p className="text-xs text-emerald-600 mt-1">Multi-day event</p>
                  )}
                </div>
              </div>

              {/* Location */}
              {event.location && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Location</p>
                    <p className="text-sm text-slate-500">{event.location}</p>
                  </div>
                </div>
              )}

              {/* Virtual Meeting Link */}
              {event.is_virtual && event.virtual_meeting_link && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <LinkIcon className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Virtual Event</p>
                    <a 
                      href={event.virtual_meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Join Meeting
                    </a>
                  </div>
                </div>
              )}

              {/* Capacity */}
              {event.max_capacity && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Users className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Capacity</p>
                    <p className="text-sm text-slate-500">
                      {event.rsvp_counts?.total_attending || 0} / {event.max_capacity} spots filled
                    </p>
                    {event.enable_waitlist && (
                      <p className="text-xs text-slate-400 mt-1">Waitlist available</p>
                    )}
                  </div>
                </div>
              )}

              {/* RSVP & Guest Counts - Two Groups */}
              <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
                {/* RSVP Responses */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="font-medium text-emerald-900 mb-2">Responses</p>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      {event.rsvp_counts?.yes?.count || 0} Yes
                    </span>
                    <span className="flex items-center gap-1 text-yellow-600">
                      <HelpCircle className="w-4 h-4" />
                      {event.rsvp_counts?.maybe?.count || 0} Maybe
                    </span>
                    <span className="flex items-center gap-1 text-red-600">
                      <XCircle className="w-4 h-4" />
                      {event.rsvp_counts?.no?.count || 0} No
                    </span>
                  </div>
                </div>
                
                {/* Guest Counts */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-900 mb-2">Expected Attendance</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-blue-700">
                      <strong>{(event.rsvp_counts?.yes?.count || 0) + (event.rsvp_counts?.yes?.guests || 0)}</strong> Adults
                    </span>
                    <span className="text-purple-700">
                      <strong>{event.rsvp_counts?.yes?.children || 0}</strong> Children
                    </span>
                  </div>
                </div>
              </div>

              {/* Cost */}
              {event.cost > 0 && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">{cs()}{event.cost.toFixed(2)}</p>
                    {event.cost_description && (
                      <p className="text-sm text-slate-500">{event.cost_description}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Dress Code */}
              {event.dress_code && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Shirt className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Dress Code</p>
                    <p className="text-sm text-slate-500">{event.dress_code}</p>
                  </div>
                </div>
              )}

              {/* What to Bring */}
              {event.what_to_bring && (
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg sm:col-span-2">
                  <Package className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">What to Bring</p>
                    <p className="text-sm text-slate-500">{event.what_to_bring}</p>
                  </div>
                </div>
              )}
            </div>

            {/* RSVP Deadline Warning */}
            {event.rsvp_deadline && !isRsvpClosed && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <Clock className="w-4 h-4 inline mr-1" />
                  RSVP by {formatDate(event.rsvp_deadline)} at {formatTime(event.rsvp_deadline)}
                </p>
              </div>
            )}

            {/* RSVP Form */}
            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-emerald-800 mb-1">Thank You!</h3>
                <p className="text-emerald-600">Your RSVP has been submitted successfully.</p>
                {rsvpData.status === 'yes' && event.max_capacity && 
                 (event.rsvp_counts?.total_attending || 0) >= event.max_capacity && (
                  <p className="text-sm text-yellow-600 mt-2">
                    Note: You've been added to the waitlist
                  </p>
                )}
              </div>
            ) : isRsvpClosed ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-red-800 mb-1">RSVP Closed</h3>
                <p className="text-red-600">The deadline to RSVP for this event has passed.</p>
              </div>
            ) : isFull ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <Users className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-red-800 mb-1">Event Full</h3>
                <p className="text-red-600">This event has reached maximum capacity.</p>
              </div>
            ) : (
              <div className="border rounded-lg p-6">
                {event.event_type === 'private' && !user && !verificationToken ? (
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Private event</h3>
                      <p className="mt-1 text-sm text-slate-600">Sign in, or confirm your current academy email to RSVP.</p>
                    </div>
                    <Button onClick={handleSignIn} className="w-full" size="lg">Sign in to RSVP</Button>
                    <div className="border-t pt-5">
                      <Label htmlFor="private-rsvp-email">Academy account email</Label>
                      <Input id="private-rsvp-email" type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} placeholder="you@example.com" className="mt-1" />
                      <Button variant="outline" onClick={handleRequestSecureLink} disabled={requestingLink || linkRequested} className="mt-3 w-full">
                        {requestingLink ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : linkRequested ? 'Secure link requested' : 'Email me a secure RSVP link'}
                      </Button>
                      <p className="mt-2 text-xs text-slate-500">For privacy, we only confirm that a link was sent if the email belongs to an active account.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">RSVP to this Event</h3>
                    {/* RSVP Status Selection */}
                <div className="mb-6">
                  <Label className="text-base font-medium mb-3 block">Will you attend? *</Label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: 'yes', label: 'Yes', icon: CheckCircle, color: 'emerald' },
                      { value: 'maybe', label: 'Maybe', icon: HelpCircle, color: 'yellow' },
                      { value: 'no', label: 'No', icon: XCircle, color: 'red' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setRsvpData(prev => ({ ...prev, status: option.value }))}
                        className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                          rsvpData.status === option.value
                            ? option.color === 'emerald' 
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : option.color === 'yellow'
                              ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                              : 'border-red-500 bg-red-50 text-red-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`rsvp-${option.value}`}
                      >
                        <option.icon className="w-5 h-5" />
                        <span className="font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {event.event_type === 'private' ? (
                  <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                    {user
                      ? <>You are RSVP&apos;ing as <strong>{user.email}</strong>.</>
                      : <>Your secure email link will RSVP using the email address tied to your academy account.</>}
                  </div>
                ) : (
                  <>
                    {/* Contact Info */}
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Your Name *</Label>
                        <Input value={rsvpData.guest_name} onChange={(e) => setRsvpData(prev => ({ ...prev, guest_name: e.target.value }))} placeholder="John Smith" className="mt-1" data-testid="rsvp-name" />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={rsvpData.guest_email} onChange={(e) => setRsvpData(prev => ({ ...prev, guest_email: e.target.value }))} placeholder="john@example.com" className="mt-1" data-testid="rsvp-email" />
                      </div>
                    </div>
                    <div className="mb-4">
                      <Label>Phone (Optional)</Label>
                      <Input type="tel" value={rsvpData.guest_phone} onChange={(e) => setRsvpData(prev => ({ ...prev, guest_phone: e.target.value }))} placeholder="(555) 123-4567" className="mt-1" />
                    </div>
                  </>
                )}

                {/* Guests & Children */}
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Additional Guests</Label>
                    <Input
                      type="number"
                      min="0"
                      value={rsvpData.num_guests}
                      onChange={(e) => setRsvpData(prev => ({ ...prev, num_guests: parseInt(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Number of adults coming with you</p>
                  </div>
                  <div>
                    <Label>Children</Label>
                    <Input
                      type="number"
                      min="0"
                      value={rsvpData.num_children}
                      onChange={(e) => setRsvpData(prev => ({ ...prev, num_children: parseInt(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Number of children attending</p>
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-6">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={rsvpData.notes}
                    onChange={(e) => setRsvpData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any dietary requirements, accessibility needs, or other notes..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Submit Button */}
                    <Button 
                      onClick={handleSubmitRsvp} 
                      disabled={submitting}
                      className="w-full"
                      size="lg"
                      data-testid="submit-rsvp-btn"
                    >
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : 'Submit RSVP'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-500">
          Powered by Delhi Capitals Cricket Academy NJ
        </div>
      </div>
    </div>
  );
};

export default PublicEventRsvp;
